import { pointFrom } from "@excalidraw/math";

import type { LocalPoint } from "@excalidraw/math";

import type { PenPathElement, PenPointHandles } from "./types";

const fmt = (n: number) => {
  const r = Math.round(n * 100) / 100;
  return Object.is(r, -0) ? "0" : `${r}`;
};

const outOf = (
  points: readonly LocalPoint[],
  handles: readonly PenPointHandles[],
  i: number,
): LocalPoint =>
  handles[i]?.out
    ? pointFrom(
        points[i][0] + handles[i]!.out![0],
        points[i][1] + handles[i]!.out![1],
      )
    : points[i];

const inOf = (
  points: readonly LocalPoint[],
  handles: readonly PenPointHandles[],
  i: number,
): LocalPoint =>
  handles[i]?.in
    ? pointFrom(
        points[i][0] + handles[i]!.in![0],
        points[i][1] + handles[i]!.in![1],
      )
    : points[i];

export const buildPenPathD = (
  points: readonly LocalPoint[],
  handles: readonly PenPointHandles[],
  closed: boolean,
): string => {
  if (points.length === 0) {
    return "";
  }
  const parts: string[] = [`M ${fmt(points[0][0])} ${fmt(points[0][1])}`];
  for (let i = 0; i < points.length - 1; i++) {
    const next = points[i + 1];
    if (handles[i]?.out || handles[i + 1]?.in) {
      const c1 = outOf(points, handles, i);
      const c2 = inOf(points, handles, i + 1);
      parts.push(
        `C ${fmt(c1[0])} ${fmt(c1[1])}, ${fmt(c2[0])} ${fmt(c2[1])}, ${fmt(
          next[0],
        )} ${fmt(next[1])}`,
      );
    } else {
      parts.push(`L ${fmt(next[0])} ${fmt(next[1])}`);
    }
  }
  return closed ? `${parts.join(" ")} Z` : parts.join(" ");
};

/** cubic [p0, c1, c2, p1] for segment i → i+1; straight → 1/3-2/3 controls */
export const segmentToCurve = (
  points: readonly LocalPoint[],
  handles: readonly PenPointHandles[],
  segmentIndex: number,
): [LocalPoint, LocalPoint, LocalPoint, LocalPoint] => {
  const i = segmentIndex;
  const p0 = points[i];
  const p1 = points[i + 1];
  if (!handles[i]?.out && !handles[i + 1]?.in) {
    const third = pointFrom<LocalPoint>(
      p0[0] + (p1[0] - p0[0]) / 3,
      p0[1] + (p1[1] - p0[1]) / 3,
    );
    const twoThirds = pointFrom<LocalPoint>(
      p0[0] + ((p1[0] - p0[0]) * 2) / 3,
      p0[1] + ((p1[1] - p0[1]) * 2) / 3,
    );
    return [p0, third, twoThirds, p1];
  }
  return [p0, outOf(points, handles, i), inOf(points, handles, i + 1), p1];
};

export const getAbsHandles = (
  element: PenPathElement,
  index: number,
): { in: LocalPoint | null; out: LocalPoint | null } => {
  const anchor = element.points[index];
  const h = element.handles[index];
  return {
    in: h?.in
      ? pointFrom<LocalPoint>(anchor[0] + h.in[0], anchor[1] + h.in[1])
      : null,
    out: h?.out
      ? pointFrom<LocalPoint>(anchor[0] + h.out[0], anchor[1] + h.out[1])
      : null,
  };
};
